// Content for the /program-information/{degree}-{program} template — an
// informational program page with the lead form living in the hero.
//
// These share the slug index and imagery with the /explore bridges (one entry
// per degree level × program), but the copy is written to inform rather than to
// sell: what the credential is, what to compare between schools, and which
// related pages to look at next.
import {
  programBridgeIndex,
  AREA_HERO,
  PROGRAM_HERO,
  DEGREE_COPY,
  type ProgramBridgeEntry,
} from './programBridges'
import { areaOfStudyData } from './areaOfStudyData'

// Publisher group used when the landing URL carries no publisher params.
export const PROGRAM_INFO_PUB_GROUP_ID = 'PCGP4731148320'

export interface RelatedLink {
  label: string
  sublabel?: string
  to: string
}

export interface ProgramInformationPage {
  slug: string
  degreeInterest: number
  degreeShort: string
  degreeLong: string
  mProgram: number
  programLabel: string
  areaId: number
  areaLabel: string
  metaTitle: string
  metaDescription: string
  heroImage: string
  heroImageAlt: string
  headline: string
  subhead: string
  aboutParagraphs: string[]
  compareItems: string[]
  whyHeading: string
  benefits: { title: string; body: string }[]
  otherLevels: RelatedLink[]
  siblings: RelatedLink[]
  closingBody: string
}

/** Every combination, reused verbatim from the bridge index. */
export const programInformationIndex: ProgramBridgeEntry[] = programBridgeIndex

const bySlug = new Map(
  programInformationIndex.map((entry) => [entry.slug, entry]),
)

const byCodes = new Map(
  programInformationIndex.map((entry) => [
    `${entry.degreeInterest}-${entry.mProgram}`,
    entry,
  ]),
)

// Numeric shorthand: /program-information/3-101 resolves to the named slug, so
// a traffic source can build the URL from the codes it already sends.
export function resolveProgramInformationCodeSlug(
  slug: string | undefined,
): string | undefined {
  if (!slug || !/^\d+-\d+$/.test(slug)) return undefined
  return byCodes.get(slug)?.slug
}

// Fields where an in-person requirement is common enough that promising a
// fully online path would be misleading.
const CLINICAL_AREAS = new Set([5, 10, 3, 6, 7])

function composePage(entry: ProgramBridgeEntry): ProgramInformationPage {
  const degree = DEGREE_COPY[entry.degreeInterest]
  const area = areaOfStudyData[entry.areaId]
  const hero =
    PROGRAM_HERO[entry.mProgram] || AREA_HERO[entry.areaId] || AREA_HERO[9]
  const program = entry.programLabel
  const programLower = program.toLowerCase()
  const areaLower = entry.areaLabel.toLowerCase()

  const aboutParagraphs = [
    `An online ${degree.long} in ${programLower} sits within ${areaLower}, and is designed for students who need to study around work and family rather than on a campus schedule.`,
    CLINICAL_AREAS.has(entry.areaId)
      ? 'Coursework, assessment, and faculty support are delivered online. Requirements vary by school — some programs in this field include an externship, practicum, or supervised hours that must be completed in person, so confirm that with each school before you enroll.'
      : 'Coursework, assessment, and faculty support are delivered online, with most of the work completed on your own schedule. Requirements vary by school, so confirm the pacing and any scheduled sessions before you enroll.',
    `${degree.pitch} ${degree.timing}.`,
    "What matters to employers is the school's accreditation and the skills you gain, not whether you attended in person or online.",
  ]

  const compareItems = [
    'Accreditation — the agency, and whether it is recognized by the U.S. Department of Education or CHEA.',
    'Program length and how many credits transfer in from prior study.',
    CLINICAL_AREAS.has(entry.areaId)
      ? 'Whether any in-person hours, labs, or externships are required.'
      : 'How much of the coursework is scheduled live versus fully self-paced.',
    'Total cost, and which federal aid, scholarships, or employer tuition benefits apply.',
    'Start dates, course pacing, and how much of the work is scheduled versus self-paced.',
    'Support included — advising, tutoring, career services, and technical help.',
  ]

  const benefits = [
    {
      title: 'Accredited schools only',
      body: `Every ${programLower} program we match you with comes from an accredited institution, so your credits and credential hold their value.`,
    },
    {
      title: 'Built around your schedule',
      body: 'Online coursework lets you keep working while you study, with start dates throughout the year rather than one intake each fall.',
    },
    {
      title: 'Aid you may qualify for',
      body: 'Accredited schools eligible for federal aid can accept the FAFSA. Ask each school what applies to your situation once you are matched.',
    },
  ]

  // "Which level do I need?" is the most common next question once someone has
  // settled on the subject.
  const otherLevels = programInformationIndex
    .filter(
      (other) =>
        other.mProgram === entry.mProgram &&
        other.degreeInterest !== entry.degreeInterest,
    )
    .map((other) => ({
      label: other.programLabel,
      sublabel: DEGREE_COPY[other.degreeInterest].short,
      to: `/program-information/${other.slug}`,
    }))

  const siblings = programInformationIndex
    .filter(
      (other) =>
        other.areaId === entry.areaId &&
        other.degreeInterest === entry.degreeInterest &&
        other.mProgram !== entry.mProgram,
    )
    .slice(0, 8)
    .map((other) => ({
      label: other.programLabel,
      to: `/program-information/${other.slug}`,
    }))

  return {
    slug: entry.slug,
    degreeInterest: entry.degreeInterest,
    degreeShort: degree.short,
    degreeLong: degree.long,
    mProgram: entry.mProgram,
    programLabel: program,
    areaId: entry.areaId,
    areaLabel: entry.areaLabel,
    metaTitle: `Online ${degree.short} in ${program} — Program Information | FindOnlineDegrees`,
    metaDescription: `What an online ${degree.long} in ${programLower} covers, what to compare between schools, and how to get matched with accredited programs enrolling now.`,
    heroImage: hero.image,
    heroImageAlt: hero.alt,
    headline: `Online ${degree.short} in ${program} Programs`,
    subhead: `Compare accredited online ${programLower} programs at the ${degree.short.toLowerCase()} level. Answer a few questions and we'll connect you with schools that fit your schedule and budget.`,
    aboutParagraphs,
    compareItems,
    whyHeading: `Why study ${programLower} online?`,
    benefits,
    otherLevels,
    siblings,
    closingBody: `Get matched with accredited schools offering online ${programLower} programs at the ${degree.short.toLowerCase()} level. No cost, and no obligation.`,
  }
}

export function getProgramInformationPage(
  slug: string | undefined,
): ProgramInformationPage | undefined {
  if (!slug) return undefined
  const entry = bySlug.get(slug.toLowerCase())
  return entry ? composePage(entry) : undefined
}
